import { FILTER_EMPTY_VALUE } from "./consultantTeamFilter";

const buildEmptyAndClause = (fields) => ({
  $and: fields.map((f) => ({ [f]: null })),
});

const isEmptyAndBranch = (part, orFields) => {
  if (!part?.$and || !orFields?.length) return false;
  return orFields.every((f) =>
    part.$and.some((p) => Object.keys(p).length === 1 && p[f] === null),
  );
};

/**
 * Build a MongoDB clause for a column filter selection.
 * Stored under filter.field in tableFilterQuery; see expandTableFilterQuery.
 * @param {object} filterCol - table column with filter config
 * @param {Array} values - selected filter values (may include FILTER_EMPTY_VALUE)
 */
export function buildColumnFilterClause(filterCol, values) {
  const filter = filterCol?.filter ?? {};
  const orFields = filter.orFields;
  const emptySelected = values.some((v) => v === FILTER_EMPTY_VALUE);
  const ids = values.filter((v) => v !== FILTER_EMPTY_VALUE);

  if (orFields?.length) {
    const orClauses = [];

    if (ids.length) {
      orFields.forEach((f) => {
        orClauses.push({ [f]: { $in: ids } });
      });
    }

    if (emptySelected) {
      orClauses.push(buildEmptyAndClause(orFields));
    }

    if (orClauses.length === 1) return orClauses[0];
    return { $or: orClauses };
  }

  if (emptySelected && ids.length) {
    return { $or: [{ $in: ids }, null] };
  }

  if (emptySelected) {
    return null;
  }

  return { $in: ids };
}

/** Clause that matches no documents — used when Deselect all is clicked. */
export function buildDeselectAllClause(filterCol) {
  const orFields = filterCol?.filter?.orFields;
  if (orFields?.length) {
    return {
      $or: orFields.map((f) => ({ [f]: { $in: [] } })),
    };
  }
  return { $in: [] };
}

const isEmptyInClause = (clause) =>
  Array.isArray(clause?.$in) && clause.$in.length === 0;

const isDeselectAllOrFieldsClause = (query, orFields) => {
  if (!query?.$or || !orFields?.length) return false;
  if (query.$or.length !== orFields.length) return false;
  return query.$or.every((part, i) => {
    const f = orFields[i];
    return isEmptyInClause(part[f]);
  });
};

/**
 * Extract selected filter values from a stored table filter clause.
 * @param {object} query - stored clause for one filter field
 * @param {object} col - table column with filter config
 */
export function getColumnFilterSelectedValues(query, col) {
  if (query === null) {
    return [FILTER_EMPTY_VALUE];
  }

  if (!query) return null;

  const orFields = col.filter?.orFields;
  const field = col.filter?.field;

  if (isEmptyInClause(query)) return [];

  if (query.$in) {
    if (query.$in.some((v) => v === FILTER_EMPTY_VALUE)) {
      const ids = query.$in.filter((v) => v !== FILTER_EMPTY_VALUE);
      return ids.length ? [...ids, FILTER_EMPTY_VALUE] : [FILTER_EMPTY_VALUE];
    }
    return query.$in;
  }

  if (orFields?.length) {
    if (isDeselectAllOrFieldsClause(query, orFields)) return [];

    if (query.$and && isEmptyAndBranch(query, orFields)) {
      return [FILTER_EMPTY_VALUE];
    }

    if (query.$or) {
      const values = new Set();

      query.$or.forEach((part) => {
        if (isEmptyAndBranch(part, orFields)) {
          values.add(FILTER_EMPTY_VALUE);
          return;
        }

        orFields.forEach((f) => {
          const branchIds = part[f]?.$in;
          if (branchIds) branchIds.forEach((id) => values.add(id));
        });
      });

      return [...values];
    }
  }

  if (query.$or && !orFields?.length) {
    const values = new Set();

    query.$or.forEach((part) => {
      if (part === null || part === FILTER_EMPTY_VALUE) {
        values.add(FILTER_EMPTY_VALUE);
      } else if (part.$in) {
        part.$in.forEach((id) => {
          if (id === FILTER_EMPTY_VALUE) values.add(FILTER_EMPTY_VALUE);
          else values.add(id);
        });
      } else if (part[field] === null) {
        values.add(FILTER_EMPTY_VALUE);
      } else if (part[field]?.$in) {
        part[field].$in.forEach((id) => values.add(id));
      }
    });

    return [...values];
  }

  return [];
}

/**
 * Expand tableFilterQuery entries into MongoDB-ready fragments for $and.
 */
export function expandTableFilterQuery(tableFilterQuery, tableSchema, getTableColumns) {
  if (!tableFilterQuery || !Object.keys(tableFilterQuery).length) {
    return tableFilterQuery;
  }

  const expanded = { ...tableFilterQuery };
  getTableColumns(tableSchema).forEach((col) => {
    const field = col.filter?.field;
    if (!field || !Object.prototype.hasOwnProperty.call(expanded, field)) return;

    const clause = expanded[field];
    if (clause?.$cleared) {
      const deselectClause = buildDeselectAllClause(col);
      delete expanded[field];
      if (deselectClause.$or) {
        expanded.$or = [...(expanded.$or ?? []), ...deselectClause.$or];
      } else {
        expanded[field] = deselectClause;
      }
      return;
    }

    if (clause === null && String(field).startsWith("_filter")) {
      delete expanded[field];
      return;
    }

    if (col.filter?.orFields?.length) {
      delete expanded[field];
      if (clause?.$or) expanded.$or = clause.$or;
      else if (clause?.$and) expanded.$and = clause.$and;
      else Object.assign(expanded, clause);
      return;
    }

    if (clause?.$or) {
      delete expanded[field];
      const parts = clause.$or.map((part) => {
        if (part === null || part === FILTER_EMPTY_VALUE) {
          return { [field]: null };
        }
        if (part.$in) return { [field]: { $in: part.$in } };
        return part;
      });
      expanded.$or = [...(expanded.$or ?? []), ...parts];
    }
  });

  return expanded;
}

export { isEmptyAndBranch, buildEmptyAndClause };
